/**
 * JS-1090b: the audit-grade outcome-ledger row table
 * (GET /api/v1/trade-ideas/outcome-ledger/rows) -- "the challengeable
 * record." Routed at /portal/trade-ideas/ledger.
 *
 * DESIGN CHOICE: the aggregate performance content (PerformancePage.tsx) is
 * composed here as a second internal tab on the SAME route, rather than a
 * separate routed page -- see PerformancePage.tsx's module comment for the
 * reasoning. Both tabs share this component's 403/loading handling.
 *
 * Authorization: gated behind the SAME internal-only allowlist as /screener
 * and the daily edition (a 403 means "not an internal user," not a bug) --
 * the InternalAccessRequired handling below mirrors EditionPage.tsx's
 * existing pattern byte-for-byte.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  useTradeIdeaLedgerRows,
  useTradeIdeaLedgerPerformance,
  type LedgerRowFiltersInput,
} from "@/domains/trade-ideas/hooks/useTradeIdeas";
import type { LedgerRow } from "@/domains/trade-ideas/lib/contracts";
import { archetypeLabel, directionView, exitRuleCopy, type ExitRuleFacts } from "@/domains/trade-ideas/lib/screenerLabels";
import { buildTickerContextLink, LEDGER_TARGET_PARAM } from "@/domains/trade-ideas/lib/tickerContextLink";
import { PerformancePage } from "@/domains/trade-ideas/pages/PerformancePage";
import { isApiError } from "@/shared/lib/apiClient";
import type { ApiFn } from "@/shared/lib/types";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { cn } from "@/shared/lib/utils";

const STATUS_OPTIONS = ["OPEN", "RESOLVED", "FLAGGED"] as const;

function pnlToneClass(value: string | null | undefined): string {
  if (value == null) return "text-muted-foreground";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed === 0) return "text-muted-foreground";
  return parsed > 0 ? "text-gain" : "text-loss";
}

function signedUsd(value: string | null | undefined): string {
  if (value == null) return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return `$${value}`;
  const sign = parsed > 0 ? "+" : "";
  return `${sign}$${value}`;
}

function holdingPeriodDays(entryTimestamp: string | null | undefined, resolutionTimestamp: string | null | undefined): string {
  if (!entryTimestamp || !resolutionTimestamp) return "—";
  const entry = new Date(entryTimestamp).getTime();
  const resolved = new Date(resolutionTimestamp).getTime();
  if (!Number.isFinite(entry) || !Number.isFinite(resolved)) return "—";
  const days = Math.round((resolved - entry) / (1000 * 60 * 60 * 24));
  return `${days}d`;
}

function structureLabel(row: LedgerRow): string {
  const archetype = archetypeLabel(row.archetype);
  const direction = directionView(row.direction);
  return `${archetype} · ${direction.label}`;
}

function exitSummary(row: LedgerRow): string {
  const facts = row.exit_rule as ExitRuleFacts | null | undefined;
  return exitRuleCopy(facts ?? null) ?? "Exit rule not stamped";
}

// --------------------------------------------------------------------------- //
// Provenance affordance -- identifiers are explicitly allowed inside a
// labeled provenance panel (per the ticket), mirroring EditionMasthead's
// Tooltip+dl pattern.
// --------------------------------------------------------------------------- //

function RowProvenance({ row }: { row: LedgerRow }) {
  const items = [
    { label: "Ledger ID", value: row.ledger_id },
    row.resolution_policy_version ? { label: "Resolution policy", value: row.resolution_policy_version } : null,
    row.exit_rule_policy_version ? { label: "Exit-rule policy", value: row.exit_rule_policy_version } : null,
    row.score_policy_versions
      ? { label: "Score policy", value: JSON.stringify(row.score_policy_versions) }
      : null,
    row.edition_as_of ? { label: "Edition as-of", value: row.edition_as_of } : null,
    row.edition_published_at ? { label: "Edition published", value: row.edition_published_at } : null,
  ].filter((item): item is { label: string; value: string } => item != null);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          role="note"
          className="cursor-help text-[10px] text-muted-foreground underline decoration-dotted underline-offset-2"
        >
          Provenance
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[360px]">
        <dl className="space-y-1">
          {items.map((item) => (
            <div key={item.label} className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">{item.label}</dt>
              <dd className="break-all font-mono text-[10px]">{item.value}</dd>
            </div>
          ))}
        </dl>
      </TooltipContent>
    </Tooltip>
  );
}

// --------------------------------------------------------------------------- //
// Filter bar -- reads/writes useSearchParams so filters are shareable.
// --------------------------------------------------------------------------- //

function FilterBar({
  filters,
  onChange,
}: {
  filters: LedgerRowFiltersInput;
  onChange: (next: LedgerRowFiltersInput) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label className="block text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          Status
        </label>
        <Select
          value={filters.status ?? "ALL"}
          onValueChange={(value) => onChange({ ...filters, status: value === "ALL" ? undefined : (value as "OPEN" | "RESOLVED" | "FLAGGED") })}
        >
          <SelectTrigger size="sm" className="mt-1 w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            {STATUS_OPTIONS.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          Archetype
        </label>
        <input
          type="text"
          value={filters.archetype ?? ""}
          onChange={(event) => onChange({ ...filters, archetype: event.target.value || undefined })}
          placeholder="e.g. long_call"
          className="mt-1 h-7 w-36 rounded-[var(--radius-sm)] border border-border bg-surface px-2 text-[12px] outline-none focus-visible:border-ring"
        />
      </div>
      <div>
        <label className="block text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          Direction
        </label>
        <Select
          value={filters.direction ?? "ALL"}
          onValueChange={(value) => onChange({ ...filters, direction: value === "ALL" ? undefined : value })}
        >
          <SelectTrigger size="sm" className="mt-1 w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="BULLISH">BULLISH</SelectItem>
            <SelectItem value="BEARISH">BEARISH</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Lane</label>
        <input
          type="text"
          value={filters.lane ?? ""}
          onChange={(event) => onChange({ ...filters, lane: event.target.value || undefined })}
          placeholder="lane"
          className="mt-1 h-7 w-28 rounded-[var(--radius-sm)] border border-border bg-surface px-2 text-[12px] outline-none focus-visible:border-ring"
        />
      </div>
      <div>
        <label className="block text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">From</label>
        <input
          type="date"
          value={filters.dateFrom ?? ""}
          onChange={(event) => onChange({ ...filters, dateFrom: event.target.value || undefined })}
          className="mt-1 h-7 w-36 rounded-[var(--radius-sm)] border border-border bg-surface px-2 text-[12px] outline-none focus-visible:border-ring"
        />
      </div>
      <div>
        <label className="block text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">To</label>
        <input
          type="date"
          value={filters.dateTo ?? ""}
          onChange={(event) => onChange({ ...filters, dateTo: event.target.value || undefined })}
          className="mt-1 h-7 w-36 rounded-[var(--radius-sm)] border border-border bg-surface px-2 text-[12px] outline-none focus-visible:border-ring"
        />
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Ledger row table.
// --------------------------------------------------------------------------- //

function LedgerRowsTable({ rows, highlightLedgerId }: { rows: LedgerRow[]; highlightLedgerId?: string | null }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Ticker</TableHead>
          <TableHead>Structure</TableHead>
          <TableHead>Entry</TableHead>
          <TableHead>Exit rule</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Resolution</TableHead>
          <TableHead>Net P&amp;L</TableHead>
          <TableHead>Holding period</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          // JS-1094 (demo blocker FIX 2): no `rowId` for a "ledger" origin --
          // this row's identity IS `ledgerId` above; see
          // tickerContextLink.ts's `TickerContextLinkInput.rowId` doc
          // comment for why passing it here (the pre-fix behavior) silently
          // lost the round-trip backlink.
          const contextLink =
            row.ticker && row.ledger_id
              ? buildTickerContextLink({ ticker: row.ticker, ledgerId: row.ledger_id, origin: "ledger" })
              : null;
          const isHighlighted = Boolean(highlightLedgerId) && row.ledger_id === highlightLedgerId;
          return (
            <TableRow
              key={row.ledger_id}
              id={`ledger-row-${row.ledger_id}`}
              className={isHighlighted ? "bg-info/10 outline outline-1 outline-info/40" : undefined}
              aria-current={isHighlighted ? "true" : undefined}
            >
              <TableCell>
                {/* JS-1097: an in-app SPA route -- Link keeps this a client
                    transition (matching every other cross-surface link:
                    PerformancePage's DrawdownPointLink, the screener drawer's
                    and edition card's own "Open ticker view"), never a raw
                    <a href> full-page reload. */}
                {contextLink ? (
                  <Link to={contextLink} className="font-semibold text-foreground hover:underline">
                    {row.ticker}
                  </Link>
                ) : (
                  <span className="font-semibold text-foreground">{row.ticker ?? "—"}</span>
                )}
              </TableCell>
              <TableCell>{structureLabel(row)}</TableCell>
              <TableCell>
                <div className="font-mono tabular-nums">{row.entry_reference_price ? `$${row.entry_reference_price}` : "—"}</div>
                <div className="text-[10px] text-muted-foreground">{row.entry_timestamp ?? "—"}</div>
              </TableCell>
              <TableCell className="max-w-[220px] whitespace-normal text-[11px] text-muted-foreground">
                {exitSummary(row)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline">{row.status}</Badge>
                  {row.status_diagnostic === "UNKNOWN_STATUS" && (
                    <Badge variant="outline" className="border-warning/30 bg-warning/10 text-warning">
                      unrecognized status
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-[11px]">{row.resolution_triggering_condition ?? "—"}</div>
                <div className="text-[10px] text-muted-foreground">{row.resolution_timestamp ?? "—"}</div>
              </TableCell>
              <TableCell className={cn("font-mono tabular-nums", pnlToneClass(row.net_pnl_usd))}>
                {signedUsd(row.net_pnl_usd)}
              </TableCell>
              <TableCell className="font-mono tabular-nums">
                {holdingPeriodDays(row.entry_timestamp, row.resolution_timestamp)}
              </TableCell>
              <TableCell>
                <RowProvenance row={row} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// --------------------------------------------------------------------------- //
// Cursor pagination -- accumulate a stack of used cursors (mirrors
// EditionPage.tsx's ArchiveRail history-stack convention) since the API only
// exposes a forward `next_cursor`, never a `prior_cursor`.
// --------------------------------------------------------------------------- //

function useCursorPagination() {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [history, setHistory] = useState<string[]>([]);

  const goNext = (nextCursor: string | null | undefined) => {
    if (!nextCursor) return;
    setHistory((prev) => [...prev, cursor ?? ""]);
    setCursor(nextCursor);
  };
  const goPrevious = () => {
    setHistory((prev) => {
      const next = [...prev];
      const previousCursor = next.pop();
      setCursor(previousCursor ? previousCursor : undefined);
      return next;
    });
  };
  const reset = () => {
    setCursor(undefined);
    setHistory([]);
  };

  return { cursor, history, goNext, goPrevious, reset };
}

function InternalAccessRequired() {
  return (
    <div className="rounded-xl border border-dashed border-border bg-surface/40 p-8 text-center">
      <h1 className="text-lg font-semibold text-foreground">Internal access required</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        The outcome ledger is an internal research surface. Your account isn't on the internal allowlist for it.
      </p>
    </div>
  );
}

export interface LedgerPageProps {
  api: ApiFn;
}

export function LedgerPage({ api }: LedgerPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Round 2 (FIX 5): an incoming ledger-target link (drawdown peak/trough,
  // ticker "back to idea") ALWAYS lands on the Rows tab -- a target is
  // meaningless on the Performance tab.
  const ledgerTarget = searchParams.get(LEDGER_TARGET_PARAM) || null;
  const [tab, setTab] = useState<"rows" | "performance">("rows");
  const pagination = useCursorPagination();
  // Round 2 (FIX 4, HIGH -- expired cursor recovery): when a signed cursor
  // expires mid-session (CURSOR_TTL_SECONDS server-side), the read 422s with
  // TRADE_IDEA_LEDGER_CURSOR_EXPIRED -- recognized below to silently clear
  // cursor/history and refetch page one, with a visible recovery message
  // rather than a bare error box.
  const [sessionExpiredNotice, setSessionExpiredNotice] = useState(false);

  const filters: LedgerRowFiltersInput = useMemo(
    () => ({
      status: (searchParams.get("status") as "OPEN" | "RESOLVED" | "FLAGGED" | null) ?? undefined,
      archetype: searchParams.get("archetype") ?? undefined,
      direction: searchParams.get("direction") ?? undefined,
      lane: searchParams.get("lane") ?? undefined,
      dateFrom: searchParams.get("date_from") ?? undefined,
      dateTo: searchParams.get("date_to") ?? undefined,
      // FIX 5: a ledger target is an EXACT-id lookup -- it supersedes every
      // other filter/cursor rather than combining with them (a highlighted
      // row must always be found regardless of whatever status/archetype/
      // etc filter happens to be active in the URL already).
      ledgerId: ledgerTarget ?? undefined,
      limit: 50,
      cursor: ledgerTarget ? undefined : pagination.cursor,
    }),
    [searchParams, ledgerTarget, pagination.cursor],
  );

  const hasActiveFilters = Boolean(
    filters.status || filters.archetype || filters.direction || filters.lane || filters.dateFrom || filters.dateTo,
  );

  const rowsQuery = useTradeIdeaLedgerRows(api, filters);
  const error = rowsQuery.error;
  const status = isApiError(error) ? error.status : null;
  const isForbidden = status === 403;
  const errorCode =
    isApiError(error) && error.details && typeof error.details === "object" && "error_code" in error.details
      ? String((error.details as { error_code?: unknown }).error_code)
      : null;
  const isCursorExpired = errorCode === "TRADE_IDEA_LEDGER_CURSOR_EXPIRED";

  // FIX 4: on a detected cursor-expiry error, clear cursor/history and show
  // the recovery notice -- the cleared cursor makes the NEXT render request
  // page one again (React Query refetches on the new, cursor-less query key).
  useEffect(() => {
    if (isCursorExpired) {
      pagination.reset();
      setSessionExpiredNotice(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fire once per expiry detection, not on every pagination identity change.
  }, [isCursorExpired]);

  const performanceQuery = useTradeIdeaLedgerPerformance(api, tab === "performance" && !isForbidden);

  const applyFilters = (next: LedgerRowFiltersInput) => {
    pagination.reset();
    setSessionExpiredNotice(false);
    const params = new URLSearchParams();
    if (next.status) params.set("status", next.status);
    if (next.archetype) params.set("archetype", next.archetype);
    if (next.direction) params.set("direction", next.direction);
    if (next.lane) params.set("lane", next.lane);
    if (next.dateFrom) params.set("date_from", next.dateFrom);
    if (next.dateTo) params.set("date_to", next.dateTo);
    // A fresh filter change clears any inbound ledger target -- the reader
    // is now browsing/filtering, not following a specific-row link.
    setSearchParams(params);
  };

  if (isForbidden) {
    return <InternalAccessRequired />;
  }

  return (
    <div className="space-y-4">
      <header className="border-b border-border pb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">Outcome ledger</h1>
          <Badge variant="secondary" className="border-info/25 bg-info/10 text-info">
            Internal · Shadow research
          </Badge>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Every published idea's resolved (or still-open) outcome, recorded verbatim — the challengeable record.
        </p>
      </header>

      <div className="flex gap-1 border-b border-border">
        <button
          type="button"
          onClick={() => setTab("rows")}
          className={cn(
            "px-3 py-2 text-[12px] font-medium",
            tab === "rows" ? "border-b-2 border-primary text-foreground" : "text-muted-foreground",
          )}
        >
          Rows
        </button>
        <button
          type="button"
          onClick={() => setTab("performance")}
          className={cn(
            "px-3 py-2 text-[12px] font-medium",
            tab === "performance" ? "border-b-2 border-primary text-foreground" : "text-muted-foreground",
          )}
        >
          Performance
        </button>
      </div>

      {tab === "rows" ? (
        <div className="space-y-3">
          {!ledgerTarget && <FilterBar filters={filters} onChange={applyFilters} />}
          {ledgerTarget && (
            <p className="text-[11px] text-muted-foreground">
              Showing one exact ledger entry (from a linked reference).{" "}
              <button
                type="button"
                onClick={() => applyFilters({})}
                className="underline decoration-dotted underline-offset-2 hover:text-foreground"
              >
                Clear and browse the full ledger
              </button>
            </p>
          )}

          {/* Round 2 FIX 4: a recognized expired-cursor error clears itself
              (see the effect above) and shows this recovery message instead
              of a bare error box -- the query re-fetches page one on the
              next render since the cursor was cleared. */}
          {sessionExpiredNotice && (
            <p className="text-[11px] text-warning" role="status">
              Session expired — returned to the latest entries.
            </p>
          )}

          {rowsQuery.isPending && <p className="text-sm text-muted-foreground">Loading ledger…</p>}
          {rowsQuery.isError && !isForbidden && !isCursorExpired && (
            <p className="text-sm text-destructive" role="alert">
              Could not load the ledger.
            </p>
          )}
          {!rowsQuery.isPending && !rowsQuery.isError && rowsQuery.data && (
            <>
              {rowsQuery.data.count === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {ledgerTarget
                    ? "That ledger entry could not be found."
                    : hasActiveFilters
                      ? "No rows match these filters."
                      : "No resolved ideas yet — the record accrues as published ideas resolve."}
                </p>
              ) : (
                <>
                  <LedgerRowsTable rows={rowsQuery.data.items} highlightLedgerId={ledgerTarget} />
                  {rowsQuery.data.items_dropped_count > 0 && (
                    <p className="text-[11px] text-warning">
                      {rowsQuery.data.items_dropped_count} row(s) could not be displayed.
                    </p>
                  )}
                  {!ledgerTarget && (
                    <div className="flex items-center justify-between">
                      <Button
                        size="sm"
                        variant="outline"
                        title={pagination.history.length === 0 ? "No previous entries" : undefined}
                        disabled={pagination.history.length === 0}
                        onClick={pagination.goPrevious}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        title={!rowsQuery.data.next_cursor ? "No more entries" : undefined}
                        disabled={!rowsQuery.data.next_cursor}
                        onClick={() => pagination.goNext(rowsQuery.data.next_cursor)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          {performanceQuery.isPending && <p className="text-sm text-muted-foreground">Loading performance…</p>}
          {performanceQuery.isError && <p className="text-sm text-destructive" role="alert">Could not load performance.</p>}
          {/* Round 2 FIX 3 (HIGH -- zero-resolved accounting): EVERY
              successful response delegates to PerformancePage, unconditionally
              -- it renders PopulationReconciliationTable (open/flagged/
              unknown-status counts all visible) even at resolved_count === 0.
              The old `resolved_count === 0 ? EmptyState : PerformancePage`
              branch here SKIPPED the reconciliation table entirely for an
              all-open/all-flagged/all-unknown ledger, hiding population
              accounting exactly when a reader most needs to see WHY nothing
              resolved yet -- see PerformancePage.tsx's own internal
              resolved_count===0 branch for the honest empty-state handling. */}
          {!performanceQuery.isPending && !performanceQuery.isError && performanceQuery.data && (
            <PerformancePage performance={performanceQuery.data} />
          )}
        </>
      )}
    </div>
  );
}
